import {
  Typography,
  Box,
  Grid,
  FormControl,
  Hidden,
  Button,
} from "@mui/material";
import dayjs from "dayjs";
import { Controller, FormProvider, useForm } from "react-hook-form";
import CustomDatePicker from "../../../../components/input/CustomDatePicker";
import CustomInput from "../../../../components/input/CustomInput";
import { UploadFileZone } from "../../../../components/input/UploadFileZone";
import CustomModal from "../../../../components/modal/CustomModal";
import { DROPDOWN_EMISSION_GROUP } from "../../../../components/emission/emission-components";
import { useEffect, useState } from "react";
import { useAuth } from "../../../../contexts/UserContext";
import { Activity } from "../../../../models/Activities";
import { useTranslation } from "react-i18next";
import ActivitiesService from "../../../../service/activities.service";
import alertService from "../../../../components/alert/alertService";
import { DataEntryRoleContext } from "../../../../contexts/DataEntryRoleContext";
import {
  AddEmissionContext,
  getEvidenceFileInfo,
} from "../../../../contexts/AddEmissionContext";
import { common, input } from "../../../../i18n";
import { getDominantYearInRange, storedDay } from "../../../../utils/dateUtils";
import { OrganizationBreadcrumbs } from "../OrganizationBreadcrumbs";
import { InputAmountTypeRadio } from "../InputAmountTypeRadio";
import { useCat3UpstreamPublisher } from "../useCat3UpstreamPublisher";

interface Props {
  open: boolean;
  onClose: () => void;
  activity?: Activity;
}

export const EditEmissionDateEntryRole = (props: Props) => {
  const { updateActivitiesAfterEdit } = DataEntryRoleContext();

  const { subOrganizationList, setInputAmountType } = AddEmissionContext();

  const { organization } = useAuth();

  const form = useForm<any>();

  const { t, i18n } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const scrollToInput = () => {
    setTimeout(() => {
      const input = document.getElementById("edit-amount");

      if (input) {
        input.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, 50);
  };

  const [year, setYear] = useState<number>(dayjs().year());

  useEffect(() => {
    if (!props.open) {
      form.reset();
      setInputAmountType("normal");
    } else {
      try {
        const startDate = dayjs(storedDay(props.activity!.startDate));

        const endDate = dayjs(storedDay(props.activity!.endDate));

        setYear(getDominantYearInRange(startDate, endDate));

        form.setValue("startDate", startDate);

        form.setValue("endDate", endDate);

        form.setValue("amount", props.activity?.amount);

        // the remark box is a Controller whose default only applies on its first mount
        form.setValue("remark", props.activity?.remark ?? "");

        form.setValue("attachment", props.activity?.attachment ?? "");

        form.setValue("attachmentRef", props.activity?.attachmentRef ?? "");

        if (props.activity?.auxiliary) {
          form.setValue("aux1", props.activity?.auxiliary.aux1 ?? "");
          form.setValue("aux2", props.activity?.auxiliary.aux2 ?? "");
          form.setValue("aux3", props.activity?.auxiliary.aux3 ?? "");
          form.setValue("aux4", props.activity?.auxiliary.aux4 ?? "");
          form.setValue("aux5", props.activity?.auxiliary.aux5 ?? "");
        }
      } catch (error) {
        // A half-populated form can submit an empty attachmentRef, which the backend reads as Remove.
        console.error("Failed to load the activity into the edit form", error);
      }

      scrollToInput();
    }
  }, [props.open, props.activity]);

  const upstreamPublisher = useCat3UpstreamPublisher(
    props.open,
    props.activity,
  );

  const attachment = form.watch("attachment");
  const startDate = form.watch("startDate");
  const amount = form.watch("amount");
  const remark = form.watch("remark");
  const attachmentRef = form.watch("attachmentRef");

  const prepareData = async () => {
    const { evidenceFileName, evidenceS3FileName } = await getEvidenceFileInfo(
      attachmentRef,
      attachment,
      dayjs(props.activity!.startDate).year(),
      subOrganizationList.find(
        (e) => e.name === props.activity?.suborganizationName,
      )!.refId,
    );

    const data: any = {
      // ✅ ส่ง ID ให้ถูกต้องตามโครงสร้าง V2
      activityRefId: props.activity?._id,
      emissionGroup: DROPDOWN_EMISSION_GROUP.find(
        (e) => e.emissionGroup === props.activity?.emissionGroup,
      )?._id,
      suborganization:
        subOrganizationList.find(
          (e) => e.name === props.activity?.suborganizationName,
        )?.id ?? 0,
      amount: amount,
      startDate: props.activity?.startDate.toString().substring(0, 10),
      endDate: props.activity?.endDate.toString().substring(0, 10),
      assetRef: props.activity?.assetRef,

      // ✅ เปลี่ยนจาก V1 ที่ส่ง String เป็นส่ง ID แทน (Emission Factor Ref)
      emissionFactorRef: props.activity?.emissionFactorRef,

      remark: remark ?? "",
      vendor: props.activity?.vendor ?? "",
      year: startDate.year(),
      evidenceFileName: evidenceFileName,
      evidenceS3FileName: evidenceS3FileName,
      isMonthly: props.activity?.generatedByTemplate ?? false, // เพิ่ม isMonthly กลับไป (เหมือนไฟล์อื่น)
    };

    const auxiliary: {
      aux1?: string;
      aux2?: string;
      aux3?: string;
      aux4?: string;
      aux5?: string;
    } = {};

    const aux1: string = form.getValues("aux1") ?? "";
    const aux2: string = form.getValues("aux2") ?? "";
    const aux3: string = form.getValues("aux3") ?? "";
    const aux4: string = form.getValues("aux4") ?? "";
    const aux5: string = form.getValues("aux5") ?? "";

    if (aux1 !== "") auxiliary.aux1 = aux1;
    if (aux2 !== "") auxiliary.aux2 = aux2;
    if (aux3 !== "") auxiliary.aux3 = aux3;
    if (aux4 !== "") auxiliary.aux4 = aux4;
    if (aux5 !== "") auxiliary.aux5 = aux5;

    if (Object.keys(auxiliary).length > 0) data.auxiliary = auxiliary;

    return data;
  };

  const onSubmit = async (values: any) => {
    let result;
    try {
      result = await ActivitiesService.update(
        props.activity?._id ?? "",
        await prepareData(),
      );
    } catch {
      // error alert already shown by service
      return;
    }

    if (result) {
      await updateActivitiesAfterEdit();
      props.onClose();
      alertService.mixin(t("edit-successfully"));
    }
  };

  const onError = (errors: any) => {
    const inputError: string[] = Object.keys(errors);

    const form: HTMLElement | null = document.getElementById(inputError[0]);

    if (form)
      form.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "start",
      });
  };

  return (
    <CustomModal
      header={<Typography variant="h1">{t("Edit-Emission")} </Typography>}
      body={
        <form onSubmit={form.handleSubmit(onSubmit, onError)}>
          <Box width={"100%"} height={"100%"}>
            <Box marginY={"20px"}>
              <OrganizationBreadcrumbs
                organization={organization?.name ?? ""}
                location={""}
                subOrganizations={props.activity?.suborganizationName ?? ""}
              />
            </Box>

            <Grid
              paddingTop={"24px"}
              container
              columnSpacing={"40px"}
              rowSpacing={"20px"}
              maxWidth={"100%"}
            >
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Controller
                    name="startDate"
                    control={form.control}
                    defaultValue={dayjs()}
                    render={({ field: { onChange, value } }) => (
                      <CustomDatePicker
                        value={value}
                        label={t("Start-Date")}
                        info={t("Start-Date")}
                        handleOnChange={onChange}
                        defaultDate={value}
                        disableFuture
                        required
                        disabled
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Controller
                    name="endDate"
                    control={form.control}
                    defaultValue={dayjs()}
                    render={({ field: { onChange, value } }) => (
                      <CustomDatePicker
                        value={value}
                        label={t("End-Date")}
                        info={t("End-Date")}
                        handleOnChange={onChange}
                        defaultDate={value}
                        required
                        disableFuture
                        disabled
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomInput
                  label={t("Asset-Name")}
                  info={t("Asset-Name-Description")}
                  value={props.activity?.assetName ?? ""}
                  disabled
                  required
                />
              </Grid>
              <Hidden mdDown>
                <Grid item xs={0} md={6}></Grid>
              </Hidden>
              <Grid item xs={12} md={6}>
                <CustomInput
                  label={t("Activity-Type")}
                  info={t("Activity-Type")}
                  value={
                    i18n.language === "th"
                      ? props.activity?.activityTypeTh
                      : props.activity?.activityTypeEn ?? ""
                  }
                  disabled
                  required
                />
              </Grid>
              <Hidden mdDown>
                <Grid item xs={0} md={6}></Grid>
              </Hidden>
              <Grid item xs={12} md={6}>
                <CustomInput
                  label={t(
                    DROPDOWN_EMISSION_GROUP.find(
                      (e) => e.emissionGroup === props.activity?.emissionGroup,
                    )?.attributeName ?? "Activity-Attribute",
                  )}
                  info={t(
                    DROPDOWN_EMISSION_GROUP.find(
                      (e) => e.emissionGroup === props.activity?.emissionGroup,
                    )?.attributeName ?? "Activity-Attribute",
                  )}
                  value={
                    i18n.language === "th"
                      ? props.activity?.activityAttributeTh
                      : props.activity?.activityAttributeEN ?? ""
                  }
                  disabled
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomInput
                  label={t("Activity-Emission-Factor-Publisher")}
                  info={t("Activity-Emission-Factor-Publisher")}
                  // ✅ แสดงชื่อ Publisher ที่ส่งมาจาก props
                  value={props.activity?.publishReference || t("Default")}
                  disabled
                  required
                />
                {upstreamPublisher && (
                  <Typography
                    component="span"
                    display="block"
                    variant="body2"
                    color="var(--gray9)"
                    lineHeight="16px"
                    mt="6px"
                    data-testid="ef-upstream-hint-generated"
                  >
                    {t("Activity-Emission-Factor-Upstream-Hint", {
                      publisher: upstreamPublisher,
                    })}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomInput
                  label={t("Vendor")}
                  info={t("Vendor")}
                  value={props.activity?.vendor ?? ""}
                  disabled
                />
              </Grid>

              <InputAmountTypeRadio
                activityGroup={props.activity?.emissionGroup}
                activityAttribute={props.activity?.activityAttributeEN}
              />

              <Grid item xs={12} md={6} id="edit-amount">
                <CustomInput
                  label={t("Unit")}
                  info={t("Unit")}
                  value={props.activity?.unit ?? ""}
                  disabled
                  required
                />
              </Grid>
              <FormProvider {...form}>
                {DROPDOWN_EMISSION_GROUP.find(
                  (e) => e.emissionGroup === props.activity?.emissionGroup,
                )?.component ?? undefined}
              </FormProvider>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Controller
                    name="remark"
                    defaultValue={props.activity?.remark ?? ""}
                    control={form.control}
                    rules={{
                      required: false,
                      maxLength: 255,
                    }}
                    render={({ field: { onChange, value } }) => (
                      <CustomInput
                        label={t("Remark")}
                        info={t("Remark")}
                        placeholder={t("Remark")}
                        value={value}
                        handleOnChange={onChange}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Controller
                name="attachment"
                defaultValue=""
                control={form.control}
                render={({ field: { onChange, value } }) => (
                  <input value={value} onChange={onChange} type="hidden" />
                )}
              />
              <Grid item xs={12}>
                <UploadFileZone
                  attachment={attachment ?? ""}
                  setAttachmentValue={(value) =>
                    form.setValue("attachment", value)
                  }
                  headline={`${t("Evidence-File")}`}
                  description={t("Attachment-Description")}
                  attachmentRef={attachmentRef}
                  setAttachmentRefValue={(value) =>
                    form.setValue("attachmentRef", value)
                  }
                  subOrganization={subOrganizationList.find(
                    (e) => e.name === props.activity?.suborganizationName,
                  )}
                  year={year}
                />
              </Grid>
              <Grid item xs={12} marginTop={"20px"}>
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  type="submit"
                >
                  {t("Save")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </form>
      }
      open={props.open}
      onClose={props.onClose}
    />
  );
};
